import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Checkbox, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { FamilyMember, ProfileInfoResponse } from 'all-in-one-shared-api';

import { getConfig } from 'configs';
import { HomeContext } from 'contexts/home/context';
import { Markdown, PrimaryButton } from 'components';
import { MainContainer, MarkdownContainer } from './components';
import { GenderProps, generateTeladocUrl } from './utils';
import LaunchingTeladocIcon from 'assets/images/teladoc/launching-teladoc-icon.svg';
import { useProfileInfoContext } from '../../contexts/profileInfo';

export const TeladocDisclaimerPage = (): JSX.Element => {
  const config = getConfig(process.env.REACT_APP_ENVIRONMENT);
  const { t, i18n } = useTranslation();
  const { state: userAccessState } = useContext(HomeContext);
  const { state } = useLocation();
  const navigate = useNavigate();

  const selectedPatient = state as FamilyMember;

  const [isDisclaimerAccepted, setIsDisclaimerAccepted] = useState<boolean>(false);
  const { state: profileInfo, isLoading } = useProfileInfoContext();

  const handleLaunchTeladoc = () => {
    if (!profileInfo) {
      return;
    }

    let selectedFamilyMember: FamilyMember | ProfileInfoResponse | undefined;
    if (selectedPatient.firstName === profileInfo?.firstName && selectedPatient.lastName === profileInfo?.lastName) {
      selectedFamilyMember = profileInfo;
    } else {
      selectedFamilyMember = profileInfo.familyMembers.find(
        (member) => member.firstName === selectedPatient.firstName && member.lastName === selectedPatient.lastName,
      );
    }

    const teladocUrl: string = generateTeladocUrl({
      baseUrl: config.baseTeladocUrl,
      policyNumber: profileInfo.policyNumber,
      memberId: profileInfo.memberId,
      memberType: selectedFamilyMember?.memberType ?? '',
      firstName: selectedFamilyMember?.firstName ?? '',
      lastName: selectedFamilyMember?.lastName ?? '',
      gender: selectedFamilyMember?.gender?.toLowerCase() as GenderProps,
      birthDate: selectedFamilyMember?.birthDate ?? '',
      email: profileInfo.email,
      countryCode: profileInfo.countryCode,
      phone: profileInfo.mobile,
      languageCode: i18n.language,
      signingKey: userAccessState.userAccess?.signedKey ?? '',
    });

    window.open(teladocUrl, '_blank');
    navigate('/home', { replace: true });
  };

  return (
    <Result
      icon={<img src={LaunchingTeladocIcon} />}
      title={t('cms.teladoc.disclaimer.heading')}
      extra={
        <MainContainer direction="vertical">
          <MarkdownContainer>
            <Markdown>{t('cms.teladoc.disclaimer.contentWeb')}</Markdown>
          </MarkdownContainer>

          <Checkbox onChange={(e) => setIsDisclaimerAccepted(e.target.checked)}>
            {t('teladoc.disclaimerLabel')}
          </Checkbox>

          <PrimaryButton
            icon={isLoading && <LoadingOutlined spin />}
            disabled={!isDisclaimerAccepted || isLoading}
            onClick={handleLaunchTeladoc}
          >
            {isLoading ? t('common.loading') : t('teladoc.launchTeladoc')}
          </PrimaryButton>
        </MainContainer>
      }
    />
  );
};
